/**
 * Custom hook for tracking user navigation events.
 * Automatically records navigation to the auth service when routes change.
 */

import { useEffect, useRef, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import authService from '../services/authService';

const useNavigationTracking = () => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const previousLocation = useRef(location.pathname);
  const sessionId = useRef(generateSessionId());

  useEffect(() => {
    // Only track navigation if user is authenticated
    if (!user) {
      return;
    }

    const currentPath = location.pathname;
    const previousPath = previousLocation.current;

    // Don't track the initial load (when previous is the same as current)
    if (previousPath === currentPath) {
      return;
    }

    // Don't track navigation to login page or home root page
    if (currentPath === '/login' || currentPath === '/') {
      return;
    }

    // Record the navigation event
    const recordNavigation = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const navigationData = {
          page_url: currentPath,
          referrer: previousPath,
          session_id: sessionId.current,
          metadata: {
            search_params: location.search,
            hash: location.hash,
            timestamp: new Date().toISOString(),
            user_agent: navigator.userAgent,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
            viewport_size: `${window.innerWidth}x${window.innerHeight}`,
            page_title: document.title
          }
        };

        await authService.recordNavigation(token, navigationData);
        console.log('✅ Navigation tracked successfully:', {
          from: previousPath,
          to: currentPath,
          sessionId: sessionId.current
        });
      } catch (error) {
        console.warn('❌ Failed to track navigation:', error);
        // Don't throw error to avoid disrupting user experience
      }
    };

    recordNavigation();

    // Update the previous location for next navigation
    previousLocation.current = currentPath;
  }, [location, user]);

  // Return session ID in case components need it
  return {
    sessionId: sessionId.current
  };
};

/**
 * Generate a unique session ID for this browser session
 * @returns {string} Session ID
 */
function generateSessionId() {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `session_${timestamp}_${random}`;
}

export default useNavigationTracking;